import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiX, FiSend, FiMic, FiVolume2, FiArrowRight, FiRefreshCw } from 'react-icons/fi';

// ── "Alexa — Your Tailoring App Helper" — floating help chat. Ask "how do
// I…" in English, Hinglish/Hindi or Arabic, by typing or by voice; it answers
// with simple steps, can read the answer aloud and can open the right page.
// `api` is the axios instance whose baseURL already points at the right side
// (here: the Super Admin API). Shows a one-time welcome on the first visit. ──

const WELCOME = {
  hi: 'Namaste! 👋 😊\n\nHello! Main Alexa hoon, aapki personal App Assistant.\n\nAap mujhe bataiye, main aapki kya madad kar sakti hoon?\n\nAgar aapko panel mein koi problem aa rahi hai, koi kaam karna hai, ya samajh nahi aa raha ki kis section mein jaana hai, toh bas mujhse pooch lijiye.\n\nMain aapko aasaan bhasha mein step-by-step samjhaungi ki kahan jaana hai, kaunsa button dabana hai aur apna kaam kaise poora karna hai.\n\nChaliye, bataiye — aaj main aapki kya madad kar sakti hoon? 😊',
  en: "Hello! 👋 😊\n\nI'm Alexa, your personal app assistant.\n\nIf something is not working, you need to get a job done, or you don't know which section to open — just ask me.\n\nI'll explain step by step, in simple words, where to go and which button to press.\n\nSo, how can I help you today? 😊",
  ar: 'مرحبًا! 👋 😊\n\nأنا Alexa، مساعدتك الشخصية في التطبيق.\n\nإذا واجهتك مشكلة أو أردت إنجاز عمل أو لم تعرف أي قسم تفتح، فقط اسألني.\n\nسأشرح لك خطوة بخطوة وبكلمات بسيطة أين تذهب وأي زر تضغط.\n\nكيف أستطيع مساعدتك اليوم؟ 😊',
};
const UI = {
  en: { title: 'Alexa — App Helper', placeholder: 'Type your question…', thinking: 'Thinking…', error: 'Could not reach the server. Check the internet and try again.', listen: 'Listening… speak now', open: 'Open', guide: 'From the help guide (AI is not available right now)', start: 'Start chatting', later: 'Maybe later', reset: 'New chat' },
  hi: { title: 'Alexa — App Helper', placeholder: 'Apna sawal likhiye…', thinking: 'Soch rahi hoon…', error: 'Server se baat nahi ho payi. Internet check karke dobara try kijiye.', listen: 'Sun rahi hoon… boliye', open: 'Kholo', guide: 'Help guide se jawab (AI abhi uplabdh nahi hai)', start: 'Baat shuru karein', later: 'Baad mein', reset: 'Nayi baat' },
  ar: { title: 'Alexa — مساعدة التطبيق', placeholder: 'اكتب سؤالك…', thinking: 'جارٍ التفكير…', error: 'تعذر الاتصال بالخادم. تحقق من الإنترنت وحاول مرة أخرى.', listen: 'جارٍ الاستماع… تكلم الآن', open: 'افتح', guide: 'من دليل المساعدة (الذكاء الاصطناعي غير متاح الآن)', start: 'ابدأ المحادثة', later: 'لاحقًا', reset: 'محادثة جديدة' },
};
const PAGE_NAME = {
  '/superadmin': 'Dashboard', '/superadmin/shops': 'All Shops', '/superadmin/config': 'Cloth & Styles',
  '/superadmin/inventory': 'Quick Sale Items', '/superadmin/fabric': 'Cloth & Cutting', '/superadmin/sales': 'Sales',
  '/superadmin/payments': 'Payments', '/superadmin/customers': 'Customers', '/superadmin/activity': 'Activity History',
  '/superadmin/staff': 'Staff & Logins', '/superadmin/reports': 'Reports', '/superadmin/app-version': 'App Version',
};
const SPEECH_LANG = { en: 'en-US', hi: 'hi-IN', ar: 'ar-SA' };

const store = {
  get: (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export default function AssistantChat({ api }) {
  const navigate = useNavigate();
  const location = useLocation();
  const firstVisit = store.get('alexa_welcome_seen', '') !== '1';
  const [open, setOpen] = useState(firstVisit);
  const [welcome, setWelcome] = useState(firstVisit); // intro card with Start / Later
  const [lang, setLang] = useState(() => store.get('assistant_lang', 'hi'));
  const [messages, setMessages] = useState([]); // { role, text, open?, source? }
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const listRef = useRef(null);
  const recRef = useRef(null);
  const t = UI[lang] || UI.en;
  const rtl = lang === 'ar';
  const SpeechRec = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    store.set('assistant_lang', lang);
    if (!open) return;
    api.get('/assistant/suggestions', { params: { lang } })
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [api, lang, open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy, listening]);

  const seenWelcome = () => { store.set('alexa_welcome_seen', '1'); setWelcome(false); };
  const close = () => { seenWelcome(); setOpen(false); window.speechSynthesis?.cancel(); };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    seenWelcome();
    const history = messages.slice(-8).map(({ role, text: tx }) => ({ role, text: tx }));
    setMessages(m => [...m, { role: 'user', text: q }]);
    setInput('');
    setBusy(true);
    try {
      const r = await api.post('/assistant/ask', {
        question: q, lang, history,
        context: { screen: PAGE_NAME[location.pathname] || location.pathname },
      });
      setMessages(m => [...m, { role: 'assistant', text: r.data.answer, open: r.data.open, source: r.data.source }]);
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', text: err.response?.data?.message || t.error }]);
    }
    setBusy(false);
  };

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/->/g, ', ').replace(/"/g, ''));
    u.lang = SPEECH_LANG[lang] || 'en-US';
    window.speechSynthesis.speak(u);
  };

  const toggleMic = () => {
    if (!SpeechRec) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SpeechRec();
    rec.lang = SPEECH_LANG[lang] || 'en-US';
    rec.interimResults = false;
    rec.onresult = (e) => { const said = e.results?.[0]?.[0]?.transcript; if (said) send(said); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={styles.fab} title="Alexa — App Helper">
        <span style={styles.avatarSm}>A</span> <span style={{ fontWeight: 600 }}>Help · Alexa</span>
      </button>
    );
  }

  return (
    <div style={styles.panel} dir={rtl ? 'rtl' : 'ltr'} role="dialog" aria-label={t.title}>
      <div style={styles.header}>
        <span style={styles.avatar}>A</span>
        <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{t.title}</div>
        {['hi', 'en', 'ar'].map(l => (
          <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l}
            style={{ ...styles.langBtn, ...(lang === l ? styles.langOn : {}) }}>
            {l === 'en' ? 'EN' : l === 'hi' ? 'हिं' : 'ع'}
          </button>
        ))}
        {messages.length > 0 && (
          <button onClick={() => setMessages([])} style={styles.iconBtn} title={t.reset}><FiRefreshCw size={16} /></button>
        )}
        <button onClick={close} style={styles.iconBtn} title="Close"><FiX size={18} /></button>
      </div>

      <div ref={listRef} style={styles.list}>
        <div style={styles.bot}>
          {WELCOME[lang] || WELCOME.en}
          {window.speechSynthesis && (
            <button onClick={() => speak(WELCOME[lang] || WELCOME.en)} style={styles.speak} title="Read aloud"><FiVolume2 size={14} /></button>
          )}
        </div>
        {welcome && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-accent btn-sm" onClick={seenWelcome}>{t.start}</button>
            <button className="btn btn-outline btn-sm" onClick={close}>{t.later}</button>
          </div>
        )}
        {messages.length === 0 && suggestions.map(s => (
          <button key={s} onClick={() => send(s)} style={styles.chip}>{s}</button>
        ))}
        {messages.map((m, i) => (
          <div key={i} style={m.role === 'user' ? styles.user : styles.bot}>
            {m.text}
            {m.role === 'assistant' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {m.open && PAGE_NAME[m.open] && (
                  <button className="btn btn-accent btn-sm" onClick={() => navigate(m.open)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {t.open}: {PAGE_NAME[m.open]} <FiArrowRight size={13} />
                  </button>
                )}
                {window.speechSynthesis && (
                  <button onClick={() => speak(m.text)} style={styles.speak} title="Read aloud"><FiVolume2 size={14} /></button>
                )}
              </div>
            )}
            {m.source === 'guide' && <div style={styles.note}>{t.guide}</div>}
          </div>
        ))}
        {busy && <div style={{ ...styles.bot, color: 'var(--text-muted)' }}>{t.thinking}</div>}
        {listening && <div style={{ ...styles.bot, color: 'var(--text-muted)' }}>🎤 {t.listen}</div>}
      </div>

      <form style={styles.inputRow} onSubmit={e => { e.preventDefault(); send(); }}>
        {SpeechRec && (
          <button type="button" onClick={toggleMic} title="Speak"
            style={{ ...styles.micBtn, background: listening ? 'var(--danger)' : 'var(--accent)' }}>
            <FiMic size={18} />
          </button>
        )}
        <input className="form-control" value={input} onChange={e => setInput(e.target.value)}
          placeholder={t.placeholder} style={{ flex: 1, fontSize: 14 }} maxLength={1000} />
        <button type="submit" className="btn btn-primary btn-icon" disabled={busy || !input.trim()} title="Send">
          <FiSend size={16} />
        </button>
      </form>
    </div>
  );
}

const styles = {
  fab: {
    position: 'fixed', right: 20, bottom: 20, zIndex: 200, display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px 10px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', fontSize: 14,
  },
  avatarSm: {
    width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)', color: '#1e293b',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
  },
  avatar: {
    width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)', color: '#1e293b',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0,
  },
  panel: {
    position: 'fixed', right: 16, bottom: 16, zIndex: 200, width: 'min(400px, calc(100vw - 32px))',
    height: 'min(600px, calc(100vh - 32px))', display: 'flex', flexDirection: 'column',
    background: 'var(--card)', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', background: 'var(--primary)', color: '#fff' },
  langBtn: {
    border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff',
    borderRadius: 6, padding: '2px 7px', fontSize: 12, cursor: 'pointer',
  },
  langOn: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#1e293b' },
  iconBtn: { border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 4, display: 'flex' },
  list: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg)' },
  bot: {
    alignSelf: 'flex-start', maxWidth: '90%', background: '#fff', border: '1px solid var(--border)',
    borderRadius: 12, padding: '9px 12px', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5,
  },
  user: {
    alignSelf: 'flex-end', maxWidth: '88%', background: 'var(--primary)', color: '#fff',
    borderRadius: 12, padding: '9px 12px', whiteSpace: 'pre-wrap', fontSize: 14,
  },
  chip: {
    alignSelf: 'flex-start', textAlign: 'start', border: '1px solid var(--accent)', background: '#fffbeb',
    color: 'var(--text)', borderRadius: 999, padding: '7px 13px', fontSize: 13.5, cursor: 'pointer',
  },
  speak: {
    display: 'inline-flex', marginInlineStart: 6, verticalAlign: 'middle', border: 'none',
    background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 2,
  },
  note: { marginTop: 6, fontSize: 11.5, color: 'var(--text-muted)', fontStyle: 'italic' },
  micBtn: {
    width: 38, height: 38, borderRadius: '50%', border: 'none', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  inputRow: { display: 'flex', gap: 6, alignItems: 'center', padding: 10, borderTop: '1px solid var(--border)' },
};
